/**
 * ProviderSelect — Selector maestro de Proveedores/Acreedores/Entidades
 * =====================================================================
 * V1.41 · Componente corporativo reutilizable (SCME reuse_potential=muy_alto)
 *
 * Uso obligatorio en TODO campo que identifique una entidad reutilizable:
 * - Proveedor · Acreedor · Aseguradora · Entidad financiera · Organismo
 * - Suministradora · Banco · Cliente (aplicable)
 *
 * Norma corporativa:
 *   ENTIDAD = SELECTOR
 *   OPERACIÓN (descripción, concepto, observaciones, notas) = TEXTO LIBRE
 *
 * Características:
 * - Autocompletado con normalización acentos/mayúsculas
 * - Botón "+ Crear nuevo" inline (sin abandonar el formulario)
 * - Bloqueo anti-duplicados: primero busca, sólo crea si no existe
 * - Compatibilidad con registros legacy (muestra "vincular a proveedor")
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { refreshCatalogs } from "./MoneyMovementFields";

// Normalizador: quita acentos/mayúsculas/espacios duplicados
const norm = (s) => (s || "")
  .toString()
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/\s+/g, " ")
  .trim();

let _cachedProviders = null;
let _cachedPromise = null;

async function fetchProviders(force = false) {
  if (!force && _cachedProviders) return _cachedProviders;
  if (_cachedPromise) return _cachedPromise;
  _cachedPromise = api.get("/providers")
    .then((r) => {
      _cachedProviders = r.data || [];
      _cachedPromise = null;
      return _cachedProviders;
    })
    .catch((e) => {
      _cachedPromise = null;
      throw e;
    });
  return _cachedPromise;
}

export function invalidateProviderCache() {
  _cachedProviders = null;
}

export default function ProviderSelect({
  value,                   // provider_id actual o null
  onChange,                // (provider_id, provider_obj) => void
  legacyName = "",         // nombre histórico si el registro es legacy y no tiene provider_id
  placeholder = "Selecciona un proveedor/acreedor…",
  required = false,
  disabled = false,
  autoFocus = false,
  testId = "provider-select",
  group = "",              // grupo opcional al crear (ej. "Aseguradoras", "Bancos", "Organismos públicos")
}) {
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { fetchProviders().then(setProviders).catch(() => {}); }, []);

  const selected = useMemo(
    () => providers.find((p) => p.id === value) || null,
    [providers, value]
  );

  const filtered = useMemo(() => {
    const q = norm(query);
    const arr = providers.filter((p) => p.active !== false);
    if (!q) return arr.slice(0, 50);
    return arr.filter((p) => norm(p.name).includes(q)).slice(0, 50);
  }, [providers, query]);

  // Sugerir "Crear" cuando el usuario ha escrito algo y NO hay coincidencia exacta
  const exactMatch = useMemo(() => {
    if (!query.trim()) return null;
    const q = norm(query);
    return providers.find((p) => norm(p.name) === q) || null;
  }, [providers, query]);

  const openCreate = () => {
    setCreating(true);
    setNewName(query.trim());
  };

  const doCreate = async () => {
    const name = (newName || "").trim();
    if (!name) return;
    // Anti-duplicado: si existe uno exacto → seleccionarlo en vez de crear
    const dup = providers.find((p) => norm(p.name) === norm(name));
    if (dup) {
      onChange(dup.id, dup);
      setOpen(false); setCreating(false); setQuery(""); setNewName("");
      toast.info(`"${dup.name}" ya existe en el catálogo — se ha seleccionado.`);
      return;
    }
    try {
      const payload = { name, active: true };
      if (group) payload.group = group;
      const r = await api.post("/providers", payload);
      invalidateProviderCache();
      refreshCatalogs();
      const list = await fetchProviders(true);
      setProviders(list);
      onChange(r.data.id, r.data);
      setOpen(false); setCreating(false); setQuery(""); setNewName("");
      toast.success(`Proveedor "${r.data.name}" creado`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const displayLabel = selected?.name
    || (legacyName ? `${legacyName} · (histórico — vincular)` : "");

  return (
    <div className="relative" data-testid={testId}>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setCreating(false); setQuery(""); }}}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testId}-trigger`}
            className={`w-full flex items-center justify-between gap-2 h-10 px-3 py-2 rounded-md border ${required && !value ? "border-red-300" : "border-input"} bg-background text-sm text-left hover:border-foreground/40 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <Building2 size={14} className="text-muted-foreground shrink-0" />
              <span className={`truncate ${!selected && !legacyName ? "text-muted-foreground" : ""} ${legacyName && !selected ? "text-amber-700 italic" : ""}`}>
                {displayLabel || placeholder}
              </span>
            </span>
            <ChevronsUpDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-md" align="start">
          {!creating ? (
            <div>
              <div className="relative border-b border-border">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={inputRef}
                  autoFocus={autoFocus}
                  data-testid={`${testId}-search`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por nombre…"
                  className="w-full h-10 pl-9 pr-3 text-sm bg-transparent focus:outline-none"
                />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Sin resultados{query.trim() ? ` para "${query}"` : ""}.
                  </div>
                ) : (
                  filtered.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`${testId}-option-${p.id}`}
                      onClick={() => { onChange(p.id, p); setOpen(false); setQuery(""); }}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-secondary/60 text-left"
                    >
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{p.name}</span>
                        {p.group && <span className="text-[10px] text-muted-foreground">{p.group}</span>}
                      </div>
                      {value === p.id && <Check size={14} className="text-primary shrink-0" />}
                    </button>
                  ))
                )}
              </div>
              <div className="border-t border-border p-2 bg-secondary/30">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  data-testid={`${testId}-create-btn`}
                  onClick={openCreate}
                  disabled={!!exactMatch}
                >
                  <Plus size={14} className="mr-1" />
                  {exactMatch ? `Ya existe: ${exactMatch.name}` : (query.trim() ? `Crear "${query.trim()}"` : "Crear nuevo proveedor")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Nuevo proveedor</div>
              <Input
                autoFocus
                data-testid={`${testId}-new-name`}
                placeholder="Nombre (ej. Agencia Tributaria (AEAT))"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doCreate()}
              />
              <div className="text-[11px] text-muted-foreground">
                Antes de crear, comprueba si ya existe con otro nombre (p. ej. "Hacienda" → busca "AEAT" o "Agencia Tributaria").
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => setCreating(false)}>Cancelar</Button>
                <Button type="button" size="sm" className="flex-1" onClick={doCreate} disabled={!newName.trim()} data-testid={`${testId}-create-confirm`}>Crear y seleccionar</Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {required && !value && !legacyName && (
        <div className="text-[11px] text-red-600 mt-1">Selecciona un proveedor del catálogo.</div>
      )}
    </div>
  );
}
